import React, { useState, useEffect } from "react";
import { Table, Select, InputNumber, Tooltip, Tag, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ModalEvaluationAnswerGroup from "../modalEvaluationAnswerGroup/modalEvaluationAnswerGroup";
import "./tableCultureAttributeAnswerGroupRelation.css";

const { Option } = Select;
const { Text } = Typography;

const TableCultureAttributeAnswerGroupRelation = ({
    relEvlCmpEag = [],
    dataEvaluationAnswerGroups = [],
    handleSelectChange,
    handleWeightChange,
    onWeightTotalChange,
    title = "Atributo cultural"
}) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [currentCmpId, setCurrentCmpId] = useState(null);
    const [weightTotal, setWeightTotal] = useState(0);

    useEffect(() => {
        // Filtrar solo los elementos que no son headers de grupo para el cálculo del peso
        const realAttributes = relEvlCmpEag.filter(item => !item.isGroupHeader);
        const total = realAttributes.reduce(
            (acc, item) => acc + (parseFloat(item.weight) || 0),
            0
        );
        setWeightTotal(total.toFixed(1));
        onWeightTotalChange(total, total === 100);
    }, [relEvlCmpEag, onWeightTotalChange]);

    const openModal = (cmpId) => {
        setCurrentCmpId(cmpId);
        setModalVisible(true);
    };

    const handleCardSelect = (eagId) => {
        if (currentCmpId) {
            handleSelectChange(currentCmpId, eagId);
            setModalVisible(false);
        }
    };

    // Agrupar los datos por cat_type (solo elementos que no son headers de grupo)
    const groupedData = relEvlCmpEag
        .filter(item => !item.isGroupHeader) // Excluir headers de grupo existentes
        .reduce((acc, item) => {
            const type = item.cat_type || 'Sin categoría';
            if (!acc[type]) {
                acc[type] = [];
            }
            acc[type].push(item);
            return acc;
        }, {});

    // Colores para diferentes tipos de categorías
    const getTypeColor = (type) => {
        const colors = {
            'Atributo de Cultura': 'blue',
            'Palancas culturales': 'green',
            'Compromiso': 'orange',
            'Sin categoría': 'default'
        };
        return colors[type] || 'purple';
    };

    const columns = [
        {
            title: title,
            dataIndex: "name",
            width: '25%',
            render: (text, record) => {
                if (record.isGroupHeader) return null;
                return text;
            },
        },
        {
            title: "Descripción",
            dataIndex: "description",
            width: '30%',
            render: (text, record) => {
                if (record.isGroupHeader) return null;
                return text;
            },
        },
        {
            title: "Tipo",
            dataIndex: "cat_type",
            width: '15%',
            render: (type, record) => {
                if (record.isGroupHeader) return null;
                return (
                    <Tag color={getTypeColor(type)}>
                        {type || 'Sin categoría'}
                    </Tag>
                );
            },
        },
        {
            title: (
                <>
                    Grupo de Respuesta{" "}
                    <Tooltip title="Haga clic en el ícono para ver más detalles">
                        <InfoCircleOutlined style={{ cursor: "pointer" }} />
                    </Tooltip>
                </>
            ),
            width: '20%',
            render: (_, record) => {
                if (record.isGroupHeader) return null;
                return (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Select
                            placeholder="Seleccione grupo"
                            value={record.eag_id}
                            dropdownMatchSelectWidth={false}
                            style={{ minWidth: 150, width: "auto", maxWidth: 200 }}
                            onChange={(value) => handleSelectChange(record.cmp_id, value)}
                        >
                            {dataEvaluationAnswerGroups.map((eag) => (
                                <Option key={eag.eag_id} value={eag.eag_id}>
                                    {eag.eag_title}
                                </Option>
                            ))}
                        </Select>
                        <Tooltip title="Ver opciones de respuesta">
                            <InfoCircleOutlined
                                style={{ cursor: "pointer", color: "#1890ff" }}
                                onClick={() => openModal(record.cmp_id)}
                            />
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            title: (
                <>
                    Peso{" "}
                    <Tooltip title="Asignar peso entre 0 y 100. Se usa para ponderar el atributo cultural.">
                        <InfoCircleOutlined />
                    </Tooltip>
                </>
            ),
            width: '10%',
            render: (_, record) => {
                if (record.isGroupHeader) return null;
                return (
                    <InputNumber
                        min={0}
                        max={100}
                        step={0.1}
                        value={record.weight}
                        onChange={(value) => handleWeightChange(record.cmp_id, value)}
                    />
                );
            },
        },
    ];

    // Crear filas expandidas con agrupación
    const expandedRowRender = (record) => null; // No usaremos esto, pero lo mantenemos para compatibilidad

    // Crear datasource con headers de grupo
    const createGroupedDataSource = () => {
        const dataSource = [];
        
        Object.entries(groupedData).forEach(([type, items], groupIndex) => {
            // Agregar header de grupo
            dataSource.push({
                key: `group-header-${groupIndex}`,
                isGroupHeader: true,
                groupType: type,
                groupCount: items.length,
                groupColor: getTypeColor(type)
            });
            
            // Agregar items del grupo
            items.forEach((item, itemIndex) => {
                dataSource.push({
                    ...item,
                    key: `${type}-${item.cmp_id || itemIndex}`,
                    isGroupHeader: false
                });
            });
        });
        
        return dataSource;
    };

    const groupedDataSource = createGroupedDataSource();

    return (
        <>
            <Table
                className="custom-green-table culture-attribute-table"
                size="small"
                dataSource={groupedDataSource}
                columns={columns}
                rowKey="key"
                pagination={false}
                rowClassName={(record) => {
                    if (record.isGroupHeader) {
                        return 'group-header-row';
                    }
                    return '';
                }}
                components={{
                    body: {
                        row: ({ children, ...restProps }) => {
                            if (restProps.record?.isGroupHeader) {
                                return (
                                    <tr {...restProps} className="group-header-row">
                                        <td colSpan={columns.length} style={{ padding: '12px 16px' }}>
                                            <div className="culture-group-divider">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <Tag 
                                                        color={restProps.record.groupColor} 
                                                        style={{ margin: 0 }}
                                                        className="culture-group-tag"
                                                    >
                                                        {restProps.record.groupType}
                                                    </Tag>
                                                    <Text style={{ color: '#666', fontSize: '12px' }}>
                                                        ({restProps.record.groupCount} atributo{restProps.record.groupCount !== 1 ? 's' : ''})
                                                    </Text>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            return <tr {...restProps}>{children}</tr>;
                        },
                    },
                }}
                footer={() => (
                    <div
                        style={{
                            textAlign: "right",
                            fontWeight: "bold",
                            color: weightTotal === "100.0" ? "green" : "red",
                        }}
                    >
                        Total: {weightTotal}%
                    </div>
                )}
            />

            <ModalEvaluationAnswerGroup
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                onSelect={handleCardSelect}
                dataEvaluationAnswerGroups={dataEvaluationAnswerGroups}
            />
        </>
    );
};

export default TableCultureAttributeAnswerGroupRelation;